import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DotPattern } from '../components/layout/DotPattern';
import { Header } from '../components/layout/Header';
import { useAuth } from '../contexts/AuthContext';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { useSessionManager } from '../hooks/useSessionManager';
import { MaintenanceMode } from '../components/common/MaintenanceMode';
import { LoginModal } from '../components/auth/LoginModal';
import { PaymentFailureReturnModal } from '../components/credits/PaymentFailureReturnModal';
import { parseDodoReturn } from '../utils/dodoReturn';

// We'll need to lift some state up or use context for these
// For now, we'll define the interface for what the Outlet context provides
export interface DashboardContextType {
    openLoginModal?: () => void;
}

export function DashboardLayout() {
    const { user, signOut, loading } = useAuth();
    const { bakim, loading: settingsLoading } = useSystemSettings();
    const location = useLocation();
    const navigate = useNavigate();

    // Session tracking - only for registered/premium users
    // @see USER_BEHAVIOR_ANALYTICS_ROADMAP.md
    useSessionManager();

    const isAnonymousUser = !user || user.userType === 'anonymous';
    const showLoginCta = !user || user.userType === 'anonymous';
    const [showLoginModal, setShowLoginModal] = React.useState(false);
    const openLoginModal = React.useCallback(() => setShowLoginModal(true), []);
    const closeLoginModal = React.useCallback(() => setShowLoginModal(false), []);
    const [showPaymentFailureModal, setShowPaymentFailureModal] = React.useState(false);
    const [paymentFailureStatus, setPaymentFailureStatus] = React.useState<'failed' | 'cancelled'>('failed');
    const [authReady, setAuthReady] = React.useState(() => !loading);
    const [settingsReady, setSettingsReady] = React.useState(() => !settingsLoading);

    React.useEffect(() => {
        if (!loading) {
            setAuthReady(true);
        }
    }, [loading]);

    React.useEffect(() => {
        if (!settingsLoading) {
            setSettingsReady(true);
        }
    }, [settingsLoading]);

    React.useEffect(() => {
        const paymentReturn = parseDodoReturn(location.search);
        if (!paymentReturn.hasDodoParams) {
            setShowPaymentFailureModal(false);
            return;
        }

        if (paymentReturn.isFailure) {
            setPaymentFailureStatus(paymentReturn.status === 'cancelled' ? 'cancelled' : 'failed');
            setShowPaymentFailureModal(true);
            return;
        }

        if (location.search !== paymentReturn.cleanedSearch) {
            navigate(
                {
                    pathname: location.pathname,
                    search: paymentReturn.cleanedSearch,
                },
                { replace: true }
            );
        }
    }, [location.pathname, location.search, navigate]);

    const handleClosePaymentFailureModal = React.useCallback(() => {
        const paymentReturn = parseDodoReturn(location.search);
        setShowPaymentFailureModal(false);
        navigate(
            {
                pathname: location.pathname,
                search: paymentReturn.cleanedSearch,
            },
            { replace: true }
        );
    }, [location.pathname, location.search, navigate]);

    // Loading state
    if (!authReady || !settingsReady) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center relative overflow-hidden">
                <DotPattern className="opacity-60" />
                <div className="text-center relative z-10 max-w-md mx-auto px-4">
                    <div className="relative">
                        <div className="absolute -inset-2 bg-gradient-to-r from-accent-primary to-accent-secondary rounded-full blur-lg opacity-30 animate-pulse-slow" />
                        <img
                            src="/arizacark.webp"
                            alt="Yükleniyor"
                            className="relative h-16 w-16 mx-auto animate-spin"
                        />
                    </div>
                    <p className="mt-6 text-glass-700 font-medium">Yükleniyor...</p>
                </div>
            </div>
        );
    }

    // Maintenance mode check
    if (bakim.aktif) {
        return <MaintenanceMode message={bakim.mesaj} />;
    }

    return (
        <div className="min-h-screen bg-white relative overflow-hidden">
            <DotPattern className="opacity-60" />

            <Header
                userId={user?.id}
                userName={user?.displayName || user?.email || undefined}
                userPhoto={user?.photoURL || undefined}
                isAnonymous={isAnonymousUser}
                userType={user?.userType ?? (isAnonymousUser ? 'anonymous' : undefined)}
                userLimits={user?.limits}
                billingProvider={user?.billingProvider}
                premiumPlatform={user?.premiumPlatform}
                dodoCustomerId={user?.dodoCustomerId}
                premiumBonus={user?.premiumBonus}
                storeSubscription={user?.storeSubscription}
                onSignOut={user ? signOut : undefined}
                onSignIn={showLoginCta ? openLoginModal : undefined}
            />

            <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-6 flex flex-col gap-4">
                <Outlet context={{ openLoginModal }} />

                {/* Footer Text */}
                <div className="flex items-center justify-center mt-4 px-4">
                    <p className="text-sm font-semibold bg-gradient-to-r from-accent-primary to-accent-secondary bg-clip-text text-transparent opacity-80">
                        Arıza Rehberim ©
                    </p>
                </div>
            </main>

            <LoginModal
                isOpen={showLoginModal}
                onClose={closeLoginModal}
            />

            <PaymentFailureReturnModal
                isOpen={showPaymentFailureModal}
                status={paymentFailureStatus}
                onClose={handleClosePaymentFailureModal}
            />
        </div>
    );
}
